import type { ActionDescriptor, TestRef } from '@krp/client-runtime'

import { DUPLICATES_WINDOWS as W } from '../windows/registerWindows'

/**
 * Реестр действий пакета `duplicates-ui` (UI-ACTIONS): что делает каждая кнопка раздела «Дубли» — для подсказки ⓘ. Описания —
 * словами магазина, без кодов и внутренних слов (проверяет тест реестра). Статус не пишется руками: он вычисляется из
 * доказательств. Тест — путь от корня репозитория и ТОЧНОЕ имя.
 */
const WEB = 'frontend/apps/web/src/tests/duplicates.test.tsx'
const PKG = 'frontend/packages/duplicates-ui/src/features/duplicates/tests/duplicates.test.tsx'

const web = (name: string): TestRef => ({ file: WEB, name })
const pkg = (name: string): TestRef => ({ file: PKG, name })

const ACCESS = 'Тот, кому выдано право «Дубли: поиск и разбор» и просмотр каталога этого источника.'
const SAVED = 'Решение сохраняется на сервере: после обновления страницы, на другом устройстве и у других сотрудников оно на месте.'

export const duplicatesActions: readonly ActionDescriptor[] = [
  {
    id: 'duplicates.source.threshold',
    windows: [W.source],
    label: 'Порог совпадения',
    summary: 'Насколько похожими должны быть наименования, чтобы попасть в список: от 80 до 100 процентов. Чем выше порог, тем меньше пар и тем они точнее.',
    effect: 'view',
    effectText: 'Порог уходит на сервер, когда вы нажимаете «Найти дубли» или «Искать заново».',
    access: ACCESS,
    evidence: { unit: [pkg('Threshold_outside_the_contract_disables_the_start_and_says_why')] },
  },
  {
    id: 'duplicates.source.scan-start',
    windows: [W.source],
    label: 'Найти дубли',
    summary: 'Запускает проверку источника: выгружает все наименования, строит карту в службе поиска и ищет похожие пары. Идёт на сервере — страницу можно закрыть и вернуться.',
    effect: 'write-local',
    effectText: 'Записывает ход проверки и найденные пары в нашу базу. Товары в источнике не меняются.',
    access: ACCESS,
    disabledWhen: 'Порог вне диапазона 80–100 % или команда уже выполняется.',
    evidence: { unit: [pkg('Scan_panel_without_a_scan_starts_with_the_chosen_threshold'), web('Scan_starts_from_the_panel_and_progress_follows_the_server')] },
  },
  {
    id: 'duplicates.source.scan-restart',
    windows: [W.source],
    label: 'Искать заново',
    summary: 'Запускает новую проверку источника с выбранным порогом. Ваши решения — «Это копия» и «Не дубли» — сохраняются; в список добавятся только новые пары.',
    effect: 'write-local',
    effectText: 'Записывает ход проверки и новые пары в нашу базу. Ранее принятые решения не меняются.',
    access: ACCESS,
    disabledWhen: 'Порог вне диапазона 80–100 % или команда уже выполняется.',
    evidence: { unit: [pkg('Finished_scan_shows_the_summary_and_offers_to_search_again')] },
  },
  {
    id: 'duplicates.source.scan-stop',
    windows: [W.source],
    label: 'Остановить',
    summary: 'Останавливает идущую проверку. Уже найденное остаётся, продолжить можно с того же места кнопкой «Продолжить».',
    effect: 'write-local',
    effectText: 'Останавливает проверку на сервере.',
    access: ACCESS,
    evidence: { unit: [pkg('Running_scan_shows_three_steps_with_numbers_and_stop')] },
  },
  {
    id: 'duplicates.source.scan-resume',
    windows: [W.source],
    label: 'Продолжить',
    summary: 'Продолжает остановленную проверку с того места, где она остановилась.',
    effect: 'write-local',
    effectText: 'Снова запускает проверку на сервере.',
    access: ACCESS,
    evidence: { unit: [pkg('Stopped_scan_can_be_resumed')] },
  },
  {
    id: 'duplicates.source.scan-retry',
    windows: [W.source],
    label: 'Повторить',
    summary: 'Проверка прервалась на ошибке. Кнопка продолжает её с того шага, где она упала, а если продолжить нельзя — начинает проверку заново.',
    effect: 'write-local',
    effectText: 'Снова запускает проверку на сервере.',
    access: ACCESS,
    evidence: { unit: [pkg('Failed_scan_shows_step_text_code_request_and_copy_and_retry')] },
  },
  {
    id: 'duplicates.source.scan-reload',
    windows: [W.source],
    label: 'Повторить',
    summary: 'Не удалось узнать, что с проверкой этого источника. Кнопка спрашивает сервер ещё раз.',
    effect: 'read',
    effectText: 'Только перечитывает.',
    evidence: { unit: [web('Failed_scan_state_shows_the_server_text_and_retry')] },
  },
  {
    id: 'duplicates.source.filter',
    windows: [W.source],
    label: 'Какие пары показывать',
    summary: 'Переключает список: все в списке, не разобранные, отмеченные копиями или помеченные «Не дубли». Рядом число пар в каждом. Выбор сохраняется в адресе страницы.',
    effect: 'view',
    access: ACCESS,
    evidence: { unit: [pkg('Filters_show_counters_and_report_the_chosen_filter'), web('Screen_is_restored_from_the_address_without_local_storage')] },
  },
  {
    id: 'duplicates.source.select-card',
    windows: [W.source],
    label: 'Выбрать товар',
    summary: 'Выбирает этот товар в паре — он будет отмечен копией. Повторное нажатие снимает выбор; нажатие на второй товар переключает. Выбор нигде не сохраняется, пока вы не нажмёте «Это копия».',
    effect: 'view',
    access: ACCESS,
    evidence: { unit: [pkg('Selecting_a_card_marks_it_with_a_frame_icon_and_the_word_and_enables_the_button')] },
  },
  {
    id: 'duplicates.source.mark-copy',
    windows: [W.source],
    label: 'Это копия',
    summary: 'Отмечает выбранный товар копией, а второй товар пары — оригиналом. Если копией уже был другой товар, отметка переходит на выбранный.',
    effect: 'write-local',
    effectText: SAVED,
    access: ACCESS,
    disabledWhen: 'Не выбран товар в паре или выбранный товар уже отмечен копией.',
    evidence: {
      unit: [pkg('Copy_button_is_disabled_until_a_card_is_selected'), web('Marking_a_copy_is_applied_at_once_and_confirmed_by_the_server')],
    },
  },
  {
    id: 'duplicates.source.unmark',
    windows: [W.source],
    label: 'Снять отметку',
    summary: 'Снимает отметку «Копия» и «Оригинал»: пара снова считается неразобранной.',
    effect: 'write-local',
    effectText: SAVED,
    access: ACCESS,
    evidence: { unit: [pkg('Marked_pair_shows_copy_and_original_badges_and_unmark_button')] },
  },
  {
    id: 'duplicates.source.not-duplicate',
    windows: [W.source],
    label: 'Не дубли',
    summary: 'Говорит, что эти два товара не дубли. Пара сразу уходит из списка и больше не предлагается; увидеть её можно в фильтре «Не дубли».',
    effect: 'write-local',
    effectText: SAVED,
    access: ACCESS,
    evidence: { unit: [web('Not_duplicate_removes_the_pair_at_once_and_the_server_confirms'), web('Rejected_command_returns_the_pair_and_shows_the_server_text')] },
  },
  {
    id: 'duplicates.source.restore',
    windows: [W.source],
    label: 'Вернуть в список',
    summary: 'Возвращает пару, помеченную «Не дубли», обратно в список неразобранных.',
    effect: 'write-local',
    effectText: SAVED,
    access: ACCESS,
    evidence: { unit: [pkg('Not_duplicate_filter_offers_to_restore_the_pair')] },
  },
  {
    id: 'duplicates.source.page',
    windows: [W.source],
    label: 'Страница списка',
    summary: 'Переходит на предыдущую или следующую страницу списка пар. Номер страницы виден в адресе.',
    effect: 'view',
    disabledWhen: 'Вы на первой или на последней странице.',
    evidence: { unit: [web('Screen_is_restored_from_the_address_without_local_storage')] },
  },
  {
    id: 'duplicates.source.reload',
    windows: [W.source],
    label: 'Повторить',
    summary: 'Список пар не пришёл. Кнопка запрашивает его у сервера ещё раз.',
    effect: 'read',
    effectText: 'Только перечитывает.',
    evidence: { unit: [web('Failed_pairs_request_shows_the_server_text_and_retry')] },
  },
  {
    id: 'duplicates.source.export-scope',
    windows: [W.source],
    label: 'Что выгружать',
    summary: 'Выбирает, что попадёт в файл: только отмеченные пары или весь список — неразобранные и отмеченные.',
    effect: 'view',
    access: ACCESS,
    evidence: { unit: [pkg('Export_buttons_send_format_and_scope')] },
  },
  {
    id: 'duplicates.source.export-file',
    windows: [W.source],
    label: 'Выгрузить',
    summary: 'Готовит файл со списком: первый товар, второй товар и кто из них дубликат. Файл — таблица Excel, простой текст, разметка или файл для программ; готовый файл сохраняется на вашем устройстве.',
    effect: 'read',
    effectText: 'Список не меняется; файл передаётся браузеру на сохранение.',
    access: ACCESS,
    disabledWhen: 'По источнику ещё не было проверки или файл уже готовится.',
    evidence: { unit: [pkg('Export_buttons_send_format_and_scope'), web('Export_downloads_the_chosen_format_and_scope_through_the_browser')] },
  },
]
